<template>
  <div>
    <!-- Spreadsheet Export UI could go here if needed -->
  </div>
</template>

<script>
import { mapStores } from 'pinia';
import { useCheckupStore } from '@frontend/ui/stores/checkupStore';
import { useAdvisorStore } from '@frontend/ui/stores/advisorStore.js';
import { newWorkbook, getWorkbookURL } from '@frontend/shared/exceljs/helpers';

export default {
  name: 'SpreadsheetExport',
  props: {
    // Any props needed from the parent component
  },
  computed: {
    ...mapStores(useCheckupStore),
    ...mapStores(useAdvisorStore),
    
    headers() {
      return {
        'X-CSRFToken': this.$root.$data.csrfToken,
      };
    },
    
    csvExportData() {
      return this.checkupStore.selectedPortfolios.map(portfolio => {
        const holdings = this.checkupStore.holdingsByPortfolio[portfolio.external_id];

        return {
          name: portfolio.name,
          holdings: holdings?.map(holding => {
            const row = {
              'Symbol/CUSIP': this.getTickerField(holding, 'symbol') || this.getUploadedSymbol(holding),
              'Name': this.getName(holding),
              'Cost Basis': holding.costbasis,
              'Quantity': holding.number_of_shares,
              'Market Value': holding.dollars,
              'Fees': this.getTickerField(holding, 'expense_ratio'),
              'Security Type': this.getSecurityType(holding),
              'Account': holding.account,
            };
            const notes = this.getNotes(holding);
            if (notes) row['Notes'] = notes;
            return row;
          }),
        };
      });
    },
    
    csvExportDataUploadedDF() {
      return this.checkupStore.selectedPortfolios.map(portfolio => {
        const uploadedDF = this.checkupStore.uploadedDFByPortfolio[portfolio.external_id];

        const holdings = Array.isArray(uploadedDF) 
          ? uploadedDF.map(holding => this.formatHoldingRow(holding)) 
          : [];

        // Only return the portfolio if it has holdings
        if (holdings.length > 0) {
          return {
            name: portfolio.name,
            external_id: portfolio.external_id,
            holdings,
          };
        }
        return null;
      }).filter(portfolio => portfolio !== null); // Filter out portfolios with no holdings
    },
    
    combinedUploadedDF() {
      return Object.values(this.checkupStore.uploadedDFByPortfolio).flat().filter(Boolean);
    }
  },
  watch: {
    // Watch uploaded_df (the actual export source). immediate: true is required because
    // this component mounts via v-if="!holdingsloading" after holdings are already loaded,
    // so a non-immediate watch never fires until a later refresh (e.g. Run Analysis).
    csvExportDataUploadedDF: {
      handler() {
        this.updateHoldingsExports();
      },
      immediate: true,
    },
    csvExportData() {
      this.updateHoldingsExports();
    },
  },
  methods: {
    // Helpers for data retrieval
    getUploadedSymbol(holding) {
      if (typeof holding.ticker === 'string' && holding.found === false) {
        return holding.ticker;
      } else {
        try {
          if (holding.metadata?.uploadedsymbol === holding.ticker.symbol && holding.metadata?.notes) {
            return holding.metadata.uploadedname;
          }
          return holding.metadata?.uploadedsymbol;
        } catch (error) {
          return holding.ticker || holding.isin || holding.cusip || '';
        }
      }
    },
    
    getName(holding) {
      if (holding.uploadedname) {
        return holding.uploadedname;
      }
      if (holding.metadata?.uploadedname && holding.metadata?.uploadedsymbol !== holding.ticker.symbol) {
        return holding.metadata.uploadedname;
      }
      return this.getTickerField(holding, 'full_name');
    },
    
    getSecurityType(holding) {
      if (holding.securitytype) return holding.securitytype;
      return this.getTickerField(holding, 'asset_type');
    },
    
    getNotes(holding) {
      if (holding.notes) {
        return holding.notes;
      }
      return holding.metadata?.notes;
    },
    
    getPurchDate(holding) {
      if (holding.purchasedate) {
        return holding.purchasedate;
      }
      return holding.metadata?.purchasedate;
    },
    
    getAcct(holding) {
      if (holding.account) {
        return holding.account;
      }
      return null;
    },
    
    getAcctTaxStatus(holding) {
      const acct_tax_status = this.getPortfolioField(holding, 'account_tax_status_string');
      if (acct_tax_status) {
        return acct_tax_status;
      }
      return holding.account_tax_status;
    },
    
    getAcctReg(holding) {
      if (holding.account_registration) {
        return holding.account_registration;
      }
      if (holding.accountregistration) {
        return holding.accountregistration;
      }
      return null;
    },
    
    getTickerField(holding, field) {
      if (typeof holding.ticker === 'object' && holding.ticker !== null) {
        return holding.ticker[field];
      }
      return null;
    },
    
    getPortfolioField(holding, field) {
      if (typeof holding === 'object' && holding.ticker !== null) {
        return holding[field];
      }
      return null;
    },
    
    // A CUSIP is 8 alphanumerics plus a numeric check digit; an ISIN is a
    // 2-letter country code, 9 alphanumerics and a check digit.
    isCusipOrIsin(value) {
      if (typeof value !== 'string') return false;
      const candidate = value.trim().toUpperCase();
      return /^[A-Z0-9]{8}\d$/.test(candidate) || /^[A-Z]{2}[A-Z0-9]{9}\d$/.test(candidate);
    },

    // Matching a bond by CUSIP overwrites uploaded_df's `ticker` with our internal
    // short bond name (e.g. "GS 0 08/10/26 MTN.TRACE"), so export the identifier
    // the customer uploaded instead — the same rule the backend holdings CSV
    // applies via format_for_csv_output(prefer_uploaded_symbol=True). Only
    // identifiers present on the uploaded row are echoed back; we never read a
    // CUSIP out of our own securities database. Non-bonds keep their resolved
    // symbol, which is the more useful identifier and keeps company names filled
    // in by Magic Ticker Fill out of this column.
    // Mirrors frontend-vite/apps/advisor/src/features/checkup/components/SpreadsheetExport.vue
    // (this Vue 2 app is deprecated; the advisor UI is served from frontend-vite).
    getExportSymbol(holding) {
      if (holding.securitytype === 'Bond' && this.isCusipOrIsin(holding.uploadedsymbol)) {
        return holding.uploadedsymbol;
      }
      return holding.ticker;
    },

    formatHoldingRow(holding) {
      const row = {
        'Symbol/CUSIP': this.getExportSymbol(holding),
        'Name': holding.uploadedname,
        'Cost Basis': holding.costbasis,
        'Quantity': holding.number_of_shares,
        'Market Value': holding.dollars,
        'Fees': holding.fees,
        'Security Type': holding.securitytype,
        'Account': this.getAcct(holding),
        'Account Tax Status': this.getAcctTaxStatus(holding),
      };
      
      const notes = this.getNotes(holding);
      if (notes) row['Notes'] = notes;
      
      const acctreg = this.getAcctReg(holding);
      if (acctreg) row['Account Registration'] = acctreg;
      
      const purchDate = this.getPurchDate(holding);
      if (purchDate) row['Purchase Date'] = purchDate;
      
      return row;
    },
    
    async createSpreadsheet(filename, holdings, activityData = null) {
      if (!holdings?.length) return null;
      console.log('Creating spreadsheet for ' + filename + ' with ' + holdings.length + ' holdings in SpreadsheetExport component.');
      
      let workbook = newWorkbook();
      
      // Create the main holdings worksheet
      workbook = this.createHoldingsWorksheet(workbook, holdings);
      
      // Create custom holdings worksheet if format exists for this advisor
      workbook = this.createCustomHoldingsWorksheet(workbook, holdings);
      
      // Add activity worksheet if data exists
      if (activityData && activityData.length > 0) {
        console.log('Adding Activity Data to spreadsheet', activityData.length);
        workbook = this.createActivityWorksheet(workbook, activityData);
      }
      
      // Gets the download url for the workbook
      const url = await getWorkbookURL(workbook);
      
      return { filename: `${filename}.xlsx`, url };
    },
    
    createHoldingsWorksheet(workbook, holdings) {
      const worksheet = workbook.addWorksheet('Holdings');
      
      // Default columns
      const columns = [
        { header: 'Notes', key: 'notes', width: 30 },
        { header: 'Symbol/CUSIP', key: 'symbol', width: 30 },
        { header: 'Name', key: 'name', width: 50 },
        { header: 'Quantity', key: 'qty', width: 30 },
        { header: 'Cost Basis', key: 'cost', width: 30 },
        { header: 'Market Value', key: 'value', width: 30 },
        { header: 'Fees', key: 'fees', width: 30 },
        { header: 'Security Type', key: 'type', width: 30 },
        { header: 'Account', key: 'account', width: 50 },
        { header: 'Account Registration', key: 'accountregistration', width: 50 },
        { header: 'Account Tax Status', key: 'account_tax_status', width: 50 },
      ];
      
      // check if any holdings include account registration
      const includePurchDate = holdings.some(asset => asset.hasOwnProperty('Purchase Date'));
      if (includePurchDate) {
        console.log('includes purchase date');
        const purchaseDateColumn = { header: 'Purchase Date', key: 'purchase_date', width: 30 };
        columns.splice(4, 0, purchaseDateColumn);
      }
      
      // Apply columns to worksheet
      worksheet.columns = columns;

      // Populate rows
      holdings.forEach(asset => {
        let fees = asset['Fees'];
        const isPercent = typeof fees === 'string' && fees.includes('%');

        if (!isNaN(parseFloat(fees)) && !isPercent) {
          fees = (parseFloat(fees) * 100).toFixed(2) + '%';
        }

        let notes = asset['Notes'] || '';
        if (notes === undefined || notes === 'nan' || notes.toLowerCase() === 'nan') {
          console.log('undefined notes');
          notes = '';
        }
        
        let accountreg = asset['Account Registration'] || 'Unavailable';
        if (accountreg === undefined || accountreg === 'nan' || accountreg.toLowerCase() === 'nan') {
          accountreg = 'Unavailable';
        }
        
        let purchase_date = asset['Purchase Date'] || '';
        if (purchase_date === undefined || purchase_date === 'nan' || purchase_date.toLowerCase() === 'nan') {
          purchase_date = '';
        }
        
        const row = {
          notes: notes, // Add notes to row only if it exists
          symbol: asset['Symbol/CUSIP'],
          name: asset['Name'],
          cost: asset['Cost Basis'],
          purchase_date: purchase_date,
          qty: asset['Quantity'],
          value: asset['Market Value'],
          account: asset['Account'],
          accountregistration: accountreg,
          account_tax_status: asset['Account Tax Status'],
          fees: fees,
          type: asset['Security Type'],
        };

        // Only include purchase date in spreadsheet if there are some not blank
        if (!includePurchDate) delete row.purchase_date;

        worksheet.addRow(row);
      });

      return workbook;
    },
    
    createCustomHoldingsWorksheet(workbook, holdings) {
      // Check if the advisor has specified a custom holdings format, if not return the worksheet as is
      if (!this.advisorStore.firmAdvisorCoreSettings?.holdings_column_format?.length) {
        console.log('No custom holdings export specified');
        return workbook;
      }
      
      console.log('Custom holdings export format found');
      const worksheet = workbook.addWorksheet('Custom Holdings Format');
      
      // Create columns from the custom format
      const columns = this.advisorStore.firmAdvisorCoreSettings.holdings_column_format.map(col => ({
        header: col.custom,  // Use the custom name as header
        key: col.original.toLowerCase().replace(/[^a-z0-9]/g, ''),  // Create safe key from original
        width: 30
      }));
      
      worksheet.columns = columns;

      // Create a mapping for easier data access
      const columnMapping = {};
      this.advisorStore.firmAdvisorCoreSettings.holdings_column_format.forEach(col => {
        columnMapping[col.original.toLowerCase().replace(/[^a-z0-9]/g, '')] = col.original;
      });

      // Safely handle custom export rules
      const rules = this.advisorStore.holdings_export_rules;
      const exportRules = Array.isArray(rules) ? rules : (rules ? [rules] : []);
      console.log('HOLDINGS EXPORT RULES:', rules);

      // Apply custom export rules to holdings data
      const processedHoldings = holdings.map(asset => {
        try {
          const processedAsset = { ...asset };
          
          exportRules.forEach(rule => {
            try {
              // Validate rule structure
              if (!rule?.conditions?.length || !rule?.action?.type || !rule?.action?.target_column) {
                console.warn('Skipping invalid rule:', rule);
                return;
              }

              // Check if all conditions match
              const conditionsMatch = rule.conditions.every(condition => {
                try {
                  if (!condition?.column || !condition?.operator) {
                    return false;
                  }

                  // Safely get value, handling undefined/null
                  const value = processedAsset[condition.column];
                  const safeValue = value === null || value === undefined ? '' : String(value);

                  // Helper function for string cleaning
                  const cleanString = (str) => String(str || '').toLowerCase().trim().replace(/\.$/, '');

                  switch (condition.operator) {
                    case 'contains':
                    case 'not_contains':
                      const cleanVal = cleanString(safeValue);
                      const cleanCondVal = cleanString(condition.value);
                      return condition.operator === 'contains' 
                        ? cleanVal.includes(cleanCondVal)
                        : !cleanVal.includes(cleanCondVal);
                    case 'equals':
                    case 'not_equals':
                      return condition.operator === 'equals'
                        ? cleanString(safeValue) === cleanString(condition.value)
                        : cleanString(safeValue) !== cleanString(condition.value);
                    case 'blank':
                      return !safeValue || safeValue.trim() === '';
                    case 'not_blank':
                      return safeValue && safeValue.trim() !== '';
                    default:
                      console.warn(`Unknown operator: ${condition.operator}`);
                      return false;
                  }
                } catch (conditionError) {
                  console.error('Error processing condition:', conditionError);
                  return false;
                }
              });

              // Apply the action if conditions match
              if (conditionsMatch) {
                switch (rule.action.type) {
                  case 'mask':
                    if (processedAsset.hasOwnProperty(rule.action.target_column)) {
                      processedAsset[rule.action.target_column] = rule.action.value;
                    }
                    break;
                  case 'transform':
                    // Future transformation logic
                    break;
                  default:
                    console.warn(`Unknown action type: ${rule.action.type}`);
                }
              }
            } catch (ruleError) {
              console.error('Error processing rule:', ruleError);
            }
          });
          return processedAsset;
        } catch (assetError) {
          console.error('Error processing asset:', assetError);
          return asset; // Return original asset if processing fails
        }
      });

      // Populate rows using the custom format
      processedHoldings.forEach(asset => {
        const row = {};
        Object.keys(columnMapping).forEach(key => {
          let value = asset[columnMapping[key]];
          
          // Apply the same formatting rules as in createHoldingsWorksheet
          if (columnMapping[key] === 'Fees') {
            const isPercent = typeof value === 'string' && value.includes('%');
            if (!isNaN(parseFloat(value)) && !isPercent) {
              value = (parseFloat(value) * 100).toFixed(2) + '%';
            }
          } else if (columnMapping[key] === 'Notes' && (value === undefined || value === 'nan' || value?.toLowerCase() === 'nan')) {
            value = '';
          } else if (columnMapping[key] === 'Account Registration' && (value === undefined || value === 'nan' || value?.toLowerCase() === 'nan')) {
            value = 'Unavailable';
          }
          
          row[key] = value;
        });
        
        worksheet.addRow(row);
      });

      return workbook;
    },
    
    createActivityWorksheet(workbook, activityData) {
      const worksheet = workbook.addWorksheet('Activity Data');
      
      // Get headers from the first row
      const headers = Object.keys(activityData[0]);
      
      // Add headers
      worksheet.addRow(headers);
      
      // Add data rows
      activityData.forEach(row => {
        worksheet.addRow(headers.map(header => row[header]));
      });

      return workbook;
    },
    
    // Main spreadsheet generation method
    async updateHoldingsExports() {
      console.log('All files in checkupStore:', this.checkupStore.files);
      
      const csvForSelectedPortfolios = await Promise.all(
        this.csvExportDataUploadedDF.map(portfolio => {
          console.log('Processing portfolio:', {
            external_id: portfolio.external_id,
            name: portfolio.name,
            numHoldings: portfolio.holdings?.length
          });
          
          // Log all potential matching conditions
          this.checkupStore.files.forEach(file => {
            console.log('Comparing:', {
              filePortfolioId: file.taskMetadata?.portfolio_id,
              targetPortfolioId: portfolio.external_id,
              matches: file.taskMetadata?.portfolio_id === portfolio.external_id
            });
          });
          
          // Find any associated activity data
          const relevantFile = this.checkupStore.files.find(
            file => file.taskMetadata?.portfolio_id === portfolio.external_id
          );
          
          const activityData = relevantFile?.taskMetadata?.activity_data;
          
          // Create the spreadsheet
          return this.createSpreadsheet(
            portfolio.name, 
            portfolio.holdings,
            activityData
          );
        })
      );
      
      // Log the results
      console.log('Generated exports:', csvForSelectedPortfolios);
      
      // Filter out any null results
      const csvExports = [...csvForSelectedPortfolios.filter(Boolean)];
      
      // Create combined holdings spreadsheet if there are multiple portfolios
      if (csvExports.length > 1) {
        const combinedHoldings = this.combinedUploadedDF.map(holding => this.formatHoldingRow(holding));
        const csvForCombined = await this.createSpreadsheet('All_Holdings', combinedHoldings);
        
        if (csvForCombined) {
          csvExports.push(csvForCombined);
        }
      }
      
      // Store the exports in the store
      this.checkupStore.csvExports = csvExports;
      
      // Emit event to notify parent
      this.$emit('exports-updated', csvExports);
    }
  }
};
</script>

<style scoped>
</style>